import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import AdminLayout from "../../../components/AdminLayout";
import "./index.css";
import { Button, Tag, Modal, Table, Dropdown, Checkbox, Image, Upload, type UploadFile, type UploadProps, Spin, Alert, Radio } from "antd";
import { useState, useEffect, useCallback, useMemo } from "react";
import { ContentDetailTableKey, emptyContentDetailData } from "../utils/contentDetailTypes";
import DetailPage from "../../Landing/DetailPage";
import type { ColumnTypes, DetailItem } from "../utils/editableTable";
import EditableCell, { EditableRow } from "../utils/editableTable";
import { CloseOutlined, ExclamationCircleOutlined, EyeOutlined, MoreOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { getBase64, type FileType } from "../utils/upload";
import { useContentDetail } from "./hooks/useContentDetail";
import { formatThaiDate } from "../../../utils/dateFormatter";
import ImageCropModal from "../../../components/ImageCropModal";

import { fileService } from "../../../services/fileService";
import { useUserInfo } from "../../../hooks/useUserInfo";

const ContentDetail: React.FC = () => {

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // API integration
  const {
    contentDetail,
    metadata,
    isLoading,
    isSaving,
    error,
    loadContent,
    saveContent,
    deleteContent,
    updateContentDetail,
    publishContent,
    createEditableVersion,
    messageContext
  } = useContentDetail(id);

  // Local state
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [publishModalVisible, setPublishModalVisible] = useState(false);
  const [detailVisible, setDetailVisible] = useState<"" | "TH" | "EN">("");
  const [previewImageOpen, setPreviewImageOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [imageFileList, setImageFileList] = useState<UploadFile[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false); // Local edit mode state
  const [isVersionView, setIsVersionView] = useState(false); // Track if viewing a specific version
  const [isCreatingEditableVersion, setIsCreatingEditableVersion] = useState(false); // Track edit version creation
  const [cropModalVisible, setCropModalVisible] = useState(false);

  // User info hook
  const { userInfo } = useUserInfo();

  // Determine user role and permissions
  const userRole = useMemo(() => {
    if (!userInfo?.roles || userInfo.roles.length === 0) return null;
    
    if (userInfo.roles.includes('super-admin')) return 'super-admin';
    if (userInfo.roles.includes('admin')) return 'admin';
    if (userInfo.roles.includes('content-owner')) return 'content-owner';
    if (userInfo.roles.includes('content-member')) return 'content-member';
    
    return null;
  }, [userInfo]);

  const userEmail = userInfo?.email;

  // Check if user can delete content (only super-admin)
  const canDeleteContent = userRole === 'super-admin';

  // Check if user has access to view/edit this content
  const hasContentAccess = useCallback(() => {
    // super-admin and admin can access all content
    if (userRole === 'super-admin' || userRole === 'admin') return true;
    
    if (!metadata || !userEmail) return false;
    
    // content-owner or content-member can access content they own or are members of
    if (userRole === 'content-owner' || userRole === 'content-member') {
      const isOwner = metadata.content_owner === userEmail;
      const isMember = metadata.content_members?.includes(userEmail);
      
      return isOwner || isMember;
    }
    
    return false;
  }, [metadata, userEmail, userRole]);

  // Load content when component mounts or id changes
  useEffect(() => {
    const versionParam = searchParams.get('version');
    const modeParam = searchParams.get('mode');
    
    if (id && id !== "new") {
      if (versionParam) {
        // Load specific version
        loadContent(id, versionParam);
        setIsEditMode(false); // Version view is always read-only
        setIsVersionView(true); // Mark as version view
      } else {
        // Load latest version
        loadContent(id);
        // Check if we should enter edit mode immediately
        if (modeParam === 'edit') {
          setIsEditMode(true);
        } else {
          setIsEditMode(false); // Always start in read-only mode for existing content
        }
        setIsVersionView(false); // Not a version view
      }
    } else if (id === "new") {
      // Initialize with empty data for new content
      updateContentDetail(emptyContentDetailData);
      setIsEditMode(true); // New content starts in edit mode
      setIsVersionView(false); // New content is not a version view
    }
  }, [id, searchParams]);

  // Check access permissions after content is loaded
  useEffect(() => {
    // Skip access check for new content
    if (id === "new") return;
    
    // Wait until metadata and userInfo are loaded
    if (!metadata || !userInfo) return;
    
    // Check if user has access
    if (!hasContentAccess()) {
      navigate('/error');
    }
  }, [metadata, userInfo, hasContentAccess, navigate, id]);

  // Initialize image file list when content detail changes
  useEffect(() => {
    if (contentDetail && contentDetail[ContentDetailTableKey.TechnologyImage]) {
      const assetSKs = contentDetail[ContentDetailTableKey.TechnologyImage].images;
      
      // Convert asset SKs to upload file format for display
      const initializeFileList = async () => {
        try {
          // Get view URLs for existing assets using asset SKs
          const response = await fileService.getViewUrls(assetSKs);
          const fileList: UploadFile[] = assetSKs.map((assetSK, index) => {
            const urlData = response.urls.find(url => url.asset_sk === assetSK);
            return {
              uid: assetSK,
              name: urlData?.filename || `image-${index + 1}`,
              status: 'done',
              url: urlData?.presigned_url,
              response: { assetId: urlData?.asset_id || assetSK },
            };
          });
          
          setImageFileList(fileList);
        } catch (error) {
          console.error('Failed to initialize image file list:', error);
          // Fallback without URLs
          const fileList: UploadFile[] = assetSKs.map((assetSK, index) => ({
            uid: assetSK,
            name: `image-${index + 1}`,
            status: 'done',
            response: { assetId: assetSK },
          }));
          
          setImageFileList(fileList);
        }
      };

      if (assetSKs.length > 0) {
        initializeFileList();
      } else {
        setImageFileList([]);
      }
    }
  }, [contentDetail]);

  // State to track the number of items for each list-enabled table (derived from contentDetail)
  const [listCounts, setListCounts] = useState<Record<string, number>>({
    painPoint: contentDetail?.painPoint?.length || 1,
    gapToImprove: contentDetail?.gapToImprove?.length || 1,
    strategicObjective: contentDetail?.strategicObjective?.length || 1,
    businessImpact: contentDetail?.businessImpact?.length || 1,
    endGoals: contentDetail?.endGoals?.length || 1,
    pillar: contentDetail?.pillar?.length || 1,
    useCases: contentDetail?.useCases?.length || 1,
    keyChallenges: contentDetail?.keyChallenges?.length || 1,
    timeline: contentDetail?.timeline?.length || 1,
  });

  // Update list counts when contentDetail changes
  useEffect(() => {
    if (contentDetail) {
      setListCounts({
        painPoint: contentDetail.painPoint?.length || 1,
        gapToImprove: contentDetail.gapToImprove?.length || 1,
        strategicObjective: contentDetail.strategicObjective?.length || 1,
        businessImpact: contentDetail.businessImpact?.length || 1,
        endGoals: contentDetail.endGoals?.length || 1,
        pillar: contentDetail.pillar?.length || 1,
        useCases: contentDetail.useCases?.length || 1,
        keyChallenges: contentDetail.keyChallenges?.length || 1,
        timeline: contentDetail.timeline?.length || 1,
      });
    }
  }, [contentDetail]);

  const defaultColumns: (ColumnTypes[number] & { editable?: boolean; dataIndex: string; })[] = [
    {
      title: 'หัวข้อ',
      dataIndex: 'name',
      width: '30%',
      render: (text: any, record: DetailItem) => {
        // If it's a header row, return the original content
        if (typeof record.key === 'string' && record.key.includes('-header-')) {
          return text;
        }

        // For regular rows, add red asterisk if required
        if (record.required && isEditMode) {
          return (
            <span>
              <span style={{ color: '#ff4d4f', marginRight: '4px' }}>*</span>
              {text}
            </span>
          );
        }

        return text;
      },
      onCell: (record: DetailItem) => {
        if (typeof record.key === 'string' && record.key.includes('-header-')) {
          return { colSpan: 2 };
        }
        return {};
      },
    },
    {
      title: 'รายละเอียด',
      dataIndex: 'value',
      editable: isEditMode,
      onCell: (record: DetailItem) => {
        if (typeof record.key === 'string' && record.key.includes('-header-')) {
          return { colSpan: 0 };
        }
        return {};
      },
    },
  ];

  const statusTag = (status: string) => {
    let color = '';
    let text = '';

    if (status === 'published') {
      color = 'green';
      text = 'เผยแพร่แล้ว';
    } else if (status === 'draft') {
      color = 'orange';
      text = 'แบบร่าง';
    }

    return <Tag color={color}>{text}</Tag>;
  }

  // In your components object:
  const components = {
    body: {
      row: EditableRow,
      cell: (props: any) => <EditableCell {...props} hasValidationError={hasFieldError(props.record?.key)} />,
    },
  };

  const columns = defaultColumns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: DetailItem) => ({
        record,
        editable: col.editable && 
                  record.editable !== false && 
                  !(metadata?.key && record.key === `${ContentDetailTableKey.Overview}-dxNo`),
        dataIndex: col.dataIndex,
        title: col.title,
        colSpan: col.onCell ? col.onCell(record)?.colSpan : 1,
        handleSave: handleSaveField,
      }),
    };
  });

  const handleSaveField = (row: DetailItem) => {
    if (!contentDetail) return;

    // Find which table this row belongs to by its key
    const key = row.key as string;
    let updated = { ...contentDetail };

    // Helper to update a value in an array of objects by index and field
    const updateArrayItem = (
      arr: any[],
      idx: number,
      field: string,
      value: any
    ) => arr.map((item, i) => (i === idx ? { ...item, [field]: value } : item));

    // Overview
    if (key.startsWith(`${ContentDetailTableKey.Overview}-`)) {
      const field = key.split('-')[1];
      updated[ContentDetailTableKey.Overview] = {
        ...updated[ContentDetailTableKey.Overview],
        [field]: row.value,
      };
    }
    // Problem Statement
    else if (key.startsWith(`${ContentDetailTableKey.ProblemStatement}-`)) {
      const lang = key.endsWith('th') ? 'statementTh' : 'statementEn';
      updated[ContentDetailTableKey.ProblemStatement] = {
        ...updated[ContentDetailTableKey.ProblemStatement],
        [lang]: row.value,
      };
    }
    // Pain Point
    else if (key.startsWith(`${ContentDetailTableKey.PainPoint}-`)) {
      const match = key.match(/painPoint-(titleTh|titleEn|descTh|descEn)-(\d+)/);
      if (match) {
        const [, field, idx] = match;
        updated.painPoint = updateArrayItem(
          updated.painPoint,
          Number(idx),
          field,
          row.value
        );
      }
    }
    // Gap To Improve
    else if (key.startsWith(`${ContentDetailTableKey.GapToImprove}-`)) {
      const match = key.match(/gapToImprove-(titleTh|titleEn|descTh|descEn)-(\d+)/);
      if (match) {
        const [, field, idx] = match;
        updated.gapToImprove = updateArrayItem(
          updated.gapToImprove,
          Number(idx),
          field,
          row.value
        );
      }
    }
    // Strategic Objective
    else if (key.startsWith(`${ContentDetailTableKey.StrategicObjective}-`)) {
      const match = key.match(/strategicObjective-(th|en)-(\d+)/);
      if (match) {
        const [, lang, idx] = match;
        const field = lang === 'th' ? 'objectiveTh' : 'objectiveEn';
        updated.strategicObjective = updateArrayItem(
          updated.strategicObjective,
          Number(idx),
          field,
          row.value
        );
      }
    }
    // Business Impact
    else if (key.startsWith(`${ContentDetailTableKey.BusinessImpact}-`)) {
      const match = key.match(/businessImpact-(th|en)-(\d+)/);
      if (match) {
        const [, lang, idx] = match;
        const field = lang === 'th' ? 'impactTh' : 'impactEn';
        updated.businessImpact = updateArrayItem(
          updated.businessImpact,
          Number(idx),
          field,
          row.value
        );
      }
    }
    // End Goals
    else if (key.startsWith(`${ContentDetailTableKey.EndGoals}-`)) {
      const match = key.match(/endGoals-(th|en)-(\d+)/);
      if (match) {
        const [, lang, idx] = match;
        const field = lang === 'th' ? 'goalTh' : 'goalEn';
        updated.endGoals = updateArrayItem(
          updated.endGoals,
          Number(idx),
          field,
          row.value
        );
      }
    }
    // Pillar
    else if (key.startsWith(`${ContentDetailTableKey.Pillar}-`)) {
      const match = key.match(/pillar-(th|en)-(\d+)/);
      if (match) {
        const [, lang, idx] = match;
        const field = lang === 'th' ? 'pillarTh' : 'pillarEn';
        updated.pillar = updateArrayItem(
          updated.pillar,
          Number(idx),
          field,
          row.value
        );
      }
    }
    // Use Cases
    else if (key.startsWith(`${ContentDetailTableKey.UseCases}-`)) {
      // Use Case Name
      const nameMatch = key.match(/useCases-name(Th|En)-(\d+)/);
      if (nameMatch) {
        const [, lang, idx] = nameMatch;
        const field = lang === 'Th' ? 'nameTh' : 'nameEn';
        updated.useCases = updateArrayItem(
          updated.useCases,
          Number(idx),
          field,
          row.value
        );
      }
      // Use Case Details
      const detailMatch = key.match(
        /useCases-(currentStepTh|currentStepEn|nextMoveTh|nextMoveEn|potentialValueTh|potentialValueEn)-(\d+)-(\d+)/
      );
      if (detailMatch) {
        const [, field, useCaseIdx, detailIdx] = detailMatch;
        updated.useCases = updated.useCases.map((item, i) =>
          i === Number(useCaseIdx)
            ? {
              ...item,
              details: Array.isArray(item.details)
                ? item.details.map((detail, dIdx) =>
                    dIdx === Number(detailIdx)
                      ? { ...detail, [field]: row.value }
                      : detail
                  )
                : item.details,
            }
            : item
        );
      }
    }
    // Technology
    else if (key.startsWith(`${ContentDetailTableKey.Technology}-`)) {
      const field = key.endsWith('titleTh') ? 'titleTh' : 'titleEn';
      updated[ContentDetailTableKey.Technology] = {
        ...updated[ContentDetailTableKey.Technology],
        [field]: row.value,
      };
    }
    // Key Challenges
    else if (key.startsWith(`${ContentDetailTableKey.KeyChallenges}-`)) {
      const match = key.match(/keyChallenges-(th|en)-(\d+)/);
      if (match) {
        const [, lang, idx] = match;
        const field = lang === 'th' ? 'challengeTh' : 'challengeEn';
        updated.keyChallenges = updateArrayItem(
          updated.keyChallenges,
          Number(idx),
          field,
          row.value
        );
      }
    }
    // Timeline
    else if (key.startsWith(`${ContentDetailTableKey.Timeline}-`)) {
      const match = key.match(/timeline-(phaseNameTh|phaseNameEn|durationTh|durationEn|status)-(\d+)/);
      if (match) {
        const [, field, idx] = match;
        updated.timeline = updateArrayItem(
          updated.timeline,
          Number(idx),
          field,
          row.value
        );
      }
    }

    updateContentDetail(updated);
  }

  // Helper function to add a new list item (updates contentDetail and listCounts)
  const addListItem = (tableType: string) => {
    if (!contentDetail || listCounts[tableType] >= 5) return;

    if (listCounts[tableType] < 5) {
      const updated = { ...contentDetail };
      switch (tableType) {
        case 'painPoint':
          updated.painPoint = [...(contentDetail.painPoint || []), { titleTh: '', titleEn: '', descTh: '', descEn: '' }];
          break;
        case 'gapToImprove':
          updated.gapToImprove = [...(contentDetail.gapToImprove || []), { titleTh: '', titleEn: '', descTh: '', descEn: '' }];
          break;
        case 'strategicObjective':
          updated.strategicObjective = [...(contentDetail.strategicObjective || []), { objectiveTh: '', objectiveEn: '' }];
          break;
        case 'businessImpact':
          updated.businessImpact = [...(contentDetail.businessImpact || []), { impactTh: '', impactEn: '' }];
          break;
        case 'endGoals':
          updated.endGoals = [...(contentDetail.endGoals || []), { goalTh: '', goalEn: '' }];
          break;
        case 'pillar':
          updated.pillar = [...(contentDetail.pillar || []), { pillarTh: '', pillarEn: '' }];
          break;
        case 'useCases':
          updated.useCases = [
            ...(contentDetail.useCases || []),
            {
              nameTh: '',
              nameEn: '',
              details: [
                {
                  currentStepTh: '',
                  currentStepEn: '',
                  nextMoveTh: '',
                  nextMoveEn: '',
                  potentialValueTh: '',
                  potentialValueEn: '',
                }
              ],
            },
          ];
          break;
        case 'keyChallenges':
          updated.keyChallenges = [...(contentDetail.keyChallenges || []), { challengeTh: '', challengeEn: '' }];
          break;
        case 'timeline':
          updated.timeline = [...(contentDetail.timeline || []), {
            phaseNameTh: '',
            phaseNameEn: '',
            durationTh: '',
            durationEn: '',
            status: 'inProgress',
          }];
          break;
      }
      updateContentDetail(updated);
      setListCounts(prev => ({ ...prev, [tableType]: prev[tableType] + 1 }));
    }
  };

  // Helper function to remove a list item (updates contentDetail and listCounts)
  const removeListItem = (tableType: string, itemIndex: number) => {
    if (!contentDetail || listCounts[tableType] <= 1) return;

    if (listCounts[tableType] > 1) {
      const updated = { ...contentDetail };
      switch (tableType) {
        case 'painPoint':
          updated.painPoint = (contentDetail.painPoint || []).filter((_, idx) => idx !== itemIndex - 1);
          break;
        case 'gapToImprove':
          updated.gapToImprove = (contentDetail.gapToImprove || []).filter((_, idx) => idx !== itemIndex - 1);
          break;
        case 'strategicObjective':
          updated.strategicObjective = (contentDetail.strategicObjective || []).filter((_, idx) => idx !== itemIndex - 1);
          break;
        case 'businessImpact':
          updated.businessImpact = (contentDetail.businessImpact || []).filter((_, idx) => idx !== itemIndex - 1);
          break;
        case 'endGoals':
          updated.endGoals = (contentDetail.endGoals || []).filter((_, idx) => idx !== itemIndex - 1);
          break;
        case 'pillar':
          updated.pillar = (contentDetail.pillar || []).filter((_, idx) => idx !== itemIndex - 1);
          break;
        case 'useCases':
          updated.useCases = (contentDetail.useCases || []).filter((_, idx) => idx !== itemIndex - 1);
          break;
        case 'keyChallenges':
          updated.keyChallenges = (contentDetail.keyChallenges || []).filter((_, idx) => idx !== itemIndex - 1);
          break;
        case 'timeline':
          updated.timeline = (contentDetail.timeline || []).filter((_, idx) => idx !== itemIndex - 1);
          break;
      }
      updateContentDetail(updated);
      setListCounts(prev => ({ ...prev, [tableType]: prev[tableType] - 1 }));
    }
  };

  // Handle edit button click - toggle edit mode
  const handleEditClick = async () => {
    if (isVersionView && metadata?.status === 'published') {
      try {
        setIsCreatingEditableVersion(true);
        let versionParam = searchParams.get('version');
        if (!versionParam) {
          versionParam = "v" + metadata?.version.toString();
        }
        if (id && versionParam) {
          // Make API call to create editable version using the hook
          const success = await createEditableVersion(id, versionParam);
          if (success) {
            navigate(`/admin/content-list/${id}?mode=edit`);
          }
        }
      } catch (error) {
        console.error('Failed to create editable version:', error);
        // The messageContext should already handle error display through the hook
      } finally {
        setIsCreatingEditableVersion(false);
      }
      return;
    }
    setIsEditMode(true);
  };

  // Handle cancel edit - return to read-only mode
  const handleCancelEdit = () => {
    setIsEditMode(false);
    // Optionally reload content to discard unsaved changes
    if (id && id !== "new") {
      loadContent(id);
    }
  };

  // Helper function to check if a field has validation error
  const hasFieldError = (fieldKey: string): boolean => {
    return validationErrors.includes(fieldKey);
  };

  // Validation function to check required fields
  const validateRequiredFields = (): { isValid: boolean; errors: string[]; fieldErrors: Record<string, string> } => {
    const errors: string[] = [];
    const fieldErrors: Record<string, string> = {};

    if (!contentDetail) {
      errors.push('ไม่มีข้อมูลคอนเทนต์');
      return { isValid: false, errors, fieldErrors };
    }

    const isEmpty = (value: string | undefined | null): boolean => {
      return !value || value.trim() === '';
    };

    // Overview validation
    const overview = contentDetail[ContentDetailTableKey.Overview];
    if (isEmpty(overview.dxNo)) {
      errors.push('DX Module Code กรุณากรอกข้อมูลให้ครบ');
      fieldErrors[`${ContentDetailTableKey.Overview}-dxNo`] = 'กรุณากรอกข้อมูลให้ครบ';
    }
    if (isEmpty(overview.titleTh)) {
      errors.push('Module Title (TH) กรุณากรอกข้อมูลให้ครบ');
      fieldErrors[`${ContentDetailTableKey.Overview}-titleTh`] = 'กรุณากรอกข้อมูลให้ครบ';
    }
    if (isEmpty(overview.titleEn)) {
      errors.push('Module Title (EN) กรุณากรอกข้อมูลให้ครบ');
      fieldErrors[`${ContentDetailTableKey.Overview}-titleEn`] = 'กรุณากรอกข้อมูลให้ครบ';
    }
    if (isEmpty(overview.shortDescTh)) {
      errors.push('Summary Description (TH) กรุณากรอกข้อมูลให้ครบ');
      fieldErrors[`${ContentDetailTableKey.Overview}-shortDescTh`] = 'กรุณากรอกข้อมูลให้ครบ';
    }
    if (isEmpty(overview.shortDescEn)) {
      errors.push('Summary Description (EN) กรุณากรอกข้อมูลให้ครบ');
      fieldErrors[`${ContentDetailTableKey.Overview}-shortDescEn`] = 'กรุณากรอกข้อมูลให้ครบ';
    }

    // Problem Statement validation
    const problem = contentDetail[ContentDetailTableKey.ProblemStatement];
    if (isEmpty(problem.statementTh)) {
      errors.push('Problem Statement (TH) กรุณากรอกข้อมูลให้ครบ');
      fieldErrors[`${ContentDetailTableKey.ProblemStatement}-th`] = 'กรุณากรอกข้อมูลให้ครบ';
    }
    if (isEmpty(problem.statementEn)) {
      errors.push('Problem Statement (EN) กรุณากรอกข้อมูลให้ครบ');
      fieldErrors[`${ContentDetailTableKey.ProblemStatement}-en`] = 'กรุณากรอกข้อมูลให้ครบ';
    }

    // Pain Point validation
    const painPoints = contentDetail[ContentDetailTableKey.PainPoint];
    painPoints.forEach((item, idx) => {
      if (isEmpty(item.titleTh)) {
        errors.push(`Pain Point รายการที่ ${idx + 1}: Title (TH) กรุณากรอกข้อมูลให้ครบ`);
        fieldErrors[`${ContentDetailTableKey.PainPoint}-titleTh-${idx}`] = 'กรุณากรอกข้อมูลให้ครบ';
      }
      if (isEmpty(item.titleEn)) {
        errors.push(`Pain Point รายการที่ ${idx + 1}: Title (EN) กรุณากรอกข้อมูลให้ครบ`);
        fieldErrors[`${ContentDetailTableKey.PainPoint}-titleEn-${idx}`] = 'กรุณากรอกข้อมูลให้ครบ';
      }
      if (isEmpty(item.descTh)) {
        errors.push(`Pain Point รายการที่ ${idx + 1}: Description (TH) กรุณากรอกข้อมูลให้ครบ`);
        fieldErrors[`${ContentDetailTableKey.PainPoint}-descTh-${idx}`] = 'กรุณากรอกข้อมูลให้ครบ';
      }
      if (isEmpty(item.descEn)) {
        errors.push(`Pain Point รายการที่ ${idx + 1}: Description (EN) กรุณากรอกข้อมูลให้ครบ`);
        fieldErrors[`${ContentDetailTableKey.PainPoint}-descEn-${idx}`] = 'กรุณากรอกข้อมูลให้ครบ';
      }
    });

    // Gap to Improve validation
    const gapToImprove = contentDetail[ContentDetailTableKey.GapToImprove];
    gapToImprove.forEach((item, idx) => {
      if (isEmpty(item.titleTh)) {
        errors.push(`Gap to Improve รายการที่ ${idx + 1}: Title (TH) กรุณากรอกข้อมูลให้ครบ`);
        fieldErrors[`${ContentDetailTableKey.GapToImprove}-titleTh-${idx}`] = 'กรุณากรอกข้อมูลให้ครบ';
      }
      if (isEmpty(item.titleEn)) {
        errors.push(`Gap to Improve รายการที่ ${idx + 1}: Title (EN) กรุณากรอกข้อมูลให้ครบ`);
        fieldErrors[`${ContentDetailTableKey.GapToImprove}-titleEn-${idx}`] = 'กรุณากรอกข้อมูลให้ครบ';
      }
      if (isEmpty(item.descTh)) {
        errors.push(`Gap to Improve รายการที่ ${idx + 1}: Description (TH) กรุณากรอกข้อมูลให้ครบ`);
        fieldErrors[`${ContentDetailTableKey.GapToImprove}-descTh-${idx}`] = 'กรุณากรอกข้อมูลให้ครบ';
      }
      if (isEmpty(item.descEn)) {
        errors.push(`Gap to Improve รายการที่ ${idx + 1}: Description (EN) กรุณากรอกข้อมูลให้ครบ`);
        fieldErrors[`${ContentDetailTableKey.GapToImprove}-descEn-${idx}`] = 'กรุณากรอกข้อมูลให้ครบ';
      }
    });

    // Strategic Objective validation
    const strategicObjectives = contentDetail[ContentDetailTableKey.StrategicObjective];
    strategicObjectives.forEach((item, idx) => {
      if (isEmpty(item.objectiveTh)) {
        errors.push(`Strategic Objectives รายการที่ ${idx + 1}: (TH) กรุณากรอกข้อมูลให้ครบ`);
        fieldErrors[`${ContentDetailTableKey.StrategicObjective}-th-${idx}`] = 'กรุณากรอกข้อมูลให้ครบ';
      }
      if (isEmpty(item.objectiveEn)) {
        errors.push(`Strategic Objectives รายการที่ ${idx + 1}: (EN) กรุณากรอกข้อมูลให้ครบ`);
        fieldErrors[`${ContentDetailTableKey.StrategicObjective}-en-${idx}`] = 'กรุณากรอกข้อมูลให้ครบ';
      }
    });

    // Business Impact validation
    const businessImpacts = contentDetail[ContentDetailTableKey.BusinessImpact];
    businessImpacts.forEach((item, idx) => {
      if (isEmpty(item.impactTh)) {
        errors.push(`Business Impacts รายการที่ ${idx + 1}: (TH) กรุณากรอกข้อมูลให้ครบ`);
        fieldErrors[`${ContentDetailTableKey.BusinessImpact}-th-${idx}`] = 'กรุณากรอกข้อมูลให้ครบ';
      }
      if (isEmpty(item.impactEn)) {
        errors.push(`Business Impacts รายการที่ ${idx + 1}: (EN) กรุณากรอกข้อมูลให้ครบ`);
        fieldErrors[`${ContentDetailTableKey.BusinessImpact}-en-${idx}`] = 'กรุณากรอกข้อมูลให้ครบ';
      }
    });

    // End Goals validation
    const endGoals = contentDetail[ContentDetailTableKey.EndGoals];
    endGoals.forEach((item, idx) => {
      if (isEmpty(item.goalTh)) {
        errors.push(`End Goals รายการที่ ${idx + 1}: (TH) กรุณากรอกข้อมูลให้ครบ`);
        fieldErrors[`${ContentDetailTableKey.EndGoals}-th-${idx}`] = 'กรุณากรอกข้อมูลให้ครบ';
      }
      if (isEmpty(item.goalEn)) {
        errors.push(`End Goals รายการที่ ${idx + 1}: (EN) กรุณากรอกข้อมูลให้ครบ`);
        fieldErrors[`${ContentDetailTableKey.EndGoals}-en-${idx}`] = 'กรุณากรอกข้อมูลให้ครบ';
      }
    });

    // Pillar validation
    const pillars = contentDetail[ContentDetailTableKey.Pillar];
    pillars.forEach((item, idx) => {
      if (isEmpty(item.pillarTh)) {
        errors.push(`Key Capabilities/Pillars รายการที่ ${idx + 1}: (TH) กรุณากรอกข้อมูลให้ครบ`);
        fieldErrors[`${ContentDetailTableKey.Pillar}-th-${idx}`] = 'กรุณากรอกข้อมูลให้ครบ';
      }
      if (isEmpty(item.pillarEn)) {
        errors.push(`Key Capabilities/Pillars รายการที่ ${idx + 1}: (EN) กรุณากรอกข้อมูลให้ครบ`);
        fieldErrors[`${ContentDetailTableKey.Pillar}-en-${idx}`] = 'กรุณากรอกข้อมูลให้ครบ';
      }
    });

    // Technology validation
    const technology = contentDetail[ContentDetailTableKey.Technology];
    if (isEmpty(technology.titleTh)) {
      errors.push('Technologies & Enablers: Title (TH) กรุณากรอกข้อมูลให้ครบ');
      fieldErrors[`${ContentDetailTableKey.Technology}-titleTh`] = 'กรุณากรอกข้อมูลให้ครบ';
    }
    if (isEmpty(technology.titleEn)) {
      errors.push('Technologies & Enablers: Title (EN) กรุณากรอกข้อมูลให้ครบ');
      fieldErrors[`${ContentDetailTableKey.Technology}-titleEn`] = 'กรุณากรอกข้อมูลให้ครบ';
    }

    // Key Challenges validation
    const keyChallenges = contentDetail[ContentDetailTableKey.KeyChallenges];
    keyChallenges.forEach((item, idx) => {
      if (isEmpty(item.challengeTh)) {
        errors.push(`Key Challenges รายการที่ ${idx + 1}: (TH) กรุณากรอกข้อมูลให้ครบ`);
        fieldErrors[`${ContentDetailTableKey.KeyChallenges}-th-${idx}`] = 'กรุณากรอกข้อมูลให้ครบ';
      }
      if (isEmpty(item.challengeEn)) {
        errors.push(`Key Challenges รายการที่ ${idx + 1}: (EN) กรุณากรอกข้อมูลให้ครบ`);
        fieldErrors[`${ContentDetailTableKey.KeyChallenges}-en-${idx}`] = 'กรุณากรอกข้อมูลให้ครบ';
      }
    });

    // Timeline validation
    const timeline = contentDetail[ContentDetailTableKey.Timeline];
    timeline.forEach((item, idx) => {
      if (isEmpty(item.phaseNameTh)) {
        errors.push(`Timeline รายการที่ ${idx + 1}: Phase Name (TH) กรุณากรอกข้อมูลให้ครบ`);
        fieldErrors[`${ContentDetailTableKey.Timeline}-phaseNameTh-${idx}`] = 'กรุณากรอกข้อมูลให้ครบ';
      }
      if (isEmpty(item.phaseNameEn)) {
        errors.push(`Timeline รายการที่ ${idx + 1}: Phase Name (EN) กรุณากรอกข้อมูลให้ครบ`);
        fieldErrors[`${ContentDetailTableKey.Timeline}-phaseNameEn-${idx}`] = 'กรุณากรอกข้อมูลให้ครบ';
      }
      if (isEmpty(item.durationTh)) {
        errors.push(`Timeline รายการที่ ${idx + 1}: Duration (TH) กรุณากรอกข้อมูลให้ครบ`);
        fieldErrors[`${ContentDetailTableKey.Timeline}-durationTh-${idx}`] = 'กรุณากรอกข้อมูลให้ครบ';
      }
      if (isEmpty(item.durationEn)) {
        errors.push(`Timeline รายการที่ ${idx + 1}: Duration (EN) กรุณากรอกข้อมูลให้ครบ`);
        fieldErrors[`${ContentDetailTableKey.Timeline}-durationEn-${idx}`] = 'กรุณากรอกข้อมูลให้ครบ';
      }
    });

    return { isValid: errors.length === 0, errors, fieldErrors };
  };

  const handleSaveDraft = useCallback(async () => {
    if (!id) return;

    try {
      setIsAutoSaving(true);
      const success = await saveContent();
      if (success && id !== "new") {
        await loadContent(id);
      }
    } catch (error) {
      console.error('Auto-save error:', error);
    } finally {
      setIsAutoSaving(false);
    }
  }, [id, saveContent, loadContent]);

  // Auto-save functionality for draft status
  useEffect(() => {
    let autoSaveInterval: number;

    if (isEditMode && metadata?.status === 'draft' && id !== "new" && !isVersionView) {
      autoSaveInterval = setInterval(() => {
        handleSaveDraft();
      }, 30000); // 30 seconds
    }

    return () => {
      if (autoSaveInterval) {
        clearInterval(autoSaveInterval);
      }
    };
  }, [isEditMode, metadata?.status, id, isVersionView, handleSaveDraft]);

  // Save function for content creation/update
  const handleSave = async (isPublish: boolean = false) => {
    if (!id) return;

    // Validate required fields only when publishing
    if (isPublish) {
      const validation = validateRequiredFields();
      if (!validation.isValid) {
        setValidationErrors(Object.keys(validation.fieldErrors));
        return;
      } else {
        setValidationErrors([]);
        // Show publish confirmation modal
        setPublishModalVisible(true);
      }
    } else {
      if (!contentDetail) return;
      setValidationErrors([]);
      updateContentDetail(contentDetail);

      try {
        const success = await saveContent();
        if (success && id !== "new") {
          await loadContent(id);
        }
      } catch (error) {
        console.error('Save error:', error);
      }
    }
  };

  // Handle publish confirmation
  const handlePublishConfirm = async () => {
    if (!id) return;
    
    try {
      const publishedVersion = await publishContent(id);
      if (publishedVersion !== '') {
        await loadContent(id);
        setPublishModalVisible(false);
        // Navigate to the same page without query parameters to reload
        navigate(`/admin/content-list/${id}?version=${publishedVersion}`, { replace: true });
        window.location.reload();
      }
    } catch (error) {
      console.error('Publish error:', error);
    }
  };

  // Cancel function to go back to content list
  const handleCancel = () => {
    navigate('/admin/content-list');
  };

  // Show loading state
  if (isLoading) {
    // return (
    //   <AdminLayout>
    //     <div style={{ padding: '20px', textAlign: 'center' }}>
    //       <Spin size="large" />
    //       <div style={{ marginTop: '16px' }}>Loading content...</div>
    //     </div>
    //   </AdminLayout>
    // );
  }

  // Show error state
  if (error) {
    return (
      <AdminLayout>
        <div style={{ padding: '20px' }}>
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            action={
              <Button size="small" danger onClick={() => navigate('/admin/content-list')}>
                Back to List
              </Button>
            }
          />
        </div>
      </AdminLayout>
    );
  }

  // Show not found for invalid content
  if (id !== "new" && !contentDetail) {
    return (
      <AdminLayout>
        <div style={{ padding: '20px' }}>
          <Alert
            message="Content Not Found"
            description="The requested content could not be found."
            type="warning"
            showIcon
            action={
              <Button type="primary" onClick={() => navigate('/admin/content-list')}>
                Back to List
              </Button>
            }
          />
        </div>
      </AdminLayout>
    );
  }

  const overviewTable = () => {
    if (!contentDetail || Object.keys(contentDetail).length === 0) return null;
    const overview = contentDetail[ContentDetailTableKey.Overview] || {};
    const dataSource: DetailItem[] = [
      {
        key: `${ContentDetailTableKey.Overview}-dxNo`,
        name: 'DX Module Code',
        value: overview.dxNo,
        required: true,
        maxLength: 10,
      },
      {
        key: `${ContentDetailTableKey.Overview}-titleTh`,
        name: 'Module Title (TH)',
        value: overview.titleTh,
        required: true,
        maxLength: 200,
      },
      {
        key: `${ContentDetailTableKey.Overview}-titleEn`,
        name: 'Module Title (EN)',
        value: overview.titleEn,
        required: true,
        maxLength: 200,
      },
      {
        key: `${ContentDetailTableKey.Overview}-shortDescTh`,
        name: 'Summary Description (TH)',
        value: overview.shortDescTh,
        required: true,
        maxLength: 200,
      },
      {
        key: `${ContentDetailTableKey.Overview}-shortDescEn`,
        name: 'Summary Description (EN)',
        value: overview.shortDescEn,
        required: true,
        maxLength: 200,
      },
    ];
    return renderTable("รายละเอียดเบื้องต้น", dataSource, false);
  }

  const problemStatementTable = () => {
    if (!contentDetail || Object.keys(contentDetail).length === 0) return null;
    const problem = contentDetail[ContentDetailTableKey.ProblemStatement];
    const dataSource: DetailItem[] = [
      {
        key: `${ContentDetailTableKey.ProblemStatement}-th`,
        name: 'Problem Statement (TH)',
        value: problem.statementTh,
        required: true,
        maxLength: 200,
      },
      {
        key: `${ContentDetailTableKey.ProblemStatement}-en`,
        name: 'Problem Statement (EN)',
        value: problem.statementEn,
        required: true,
        maxLength: 200,
      },
    ];
    return renderTable("Before Transformation : Problem Statement", dataSource, false);
  }

  const painPointTable = () => {
    if (!contentDetail || Object.keys(contentDetail).length === 0) return null;
    const items = contentDetail[ContentDetailTableKey.PainPoint];
    if (items.length === 0) {
      items.push({
        titleTh: "",
        titleEn: "",
        descTh: "",
        descEn: ""
      });
    }
    const dataSource: DetailItem[] = items.map((item, idx) => [
      {
        key: `${ContentDetailTableKey.PainPoint}-header-${idx}`,
        name: (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <span>รายการที่ {idx + 1}</span>
            {isEditMode && items.length > 1 && (
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => removeListItem('painPoint', idx + 1)}
              >
                ลบรายการ
              </Button>
            )}
          </div>
        ),
        value: '',
      },
      {
        key: `${ContentDetailTableKey.PainPoint}-titleTh-${idx}`,
        name: 'Title (TH)',
        value: item.titleTh,
        required: true,
        maxLength: 200,
      },
      {
        key: `${ContentDetailTableKey.PainPoint}-titleEn-${idx}`,
        name: 'Title (EN)',
        value: item.titleEn,
        required: true,
        maxLength: 200,
      },
      {
        key: `${ContentDetailTableKey.PainPoint}-descTh-${idx}`,
        name: 'Description (TH)',
        value: item.descTh,
        required: true,
        maxLength: 200,
      },
      {
        key: `${ContentDetailTableKey.PainPoint}-descEn-${idx}`,
        name: 'Description (EN)',
        value: item.descEn,
        required: true,
        maxLength: 200,
      },
    ]).flat();
    return renderTable("Before Transformation : Pain Points", dataSource, true, 'painPoint');
  }

  const GapToImproveTable = () => {
    if (!contentDetail || Object.keys(contentDetail).length === 0) return null;
    const items = contentDetail[ContentDetailTableKey.GapToImprove];
    if (!items || items.length === 0) {
      // empty "" item to prompt user to fill in
      items.push({ titleTh: '', titleEn: '', descTh: '', descEn: '' });
    }
    const dataSource: DetailItem[] = items.map((item, idx) => [
      {
        key: `${ContentDetailTableKey.GapToImprove}-header-${idx}`,
        name: (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <span>รายการที่ {idx + 1}</span>
            {isEditMode && items.length > 1 && (
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => removeListItem('gapToImprove', idx + 1)}
              >
                ลบรายการ
              </Button>
            )}
          </div>
        ),
        value: '',
      },
      {
        key: `${ContentDetailTableKey.GapToImprove}-titleTh-${idx}`,
        name: 'Title (TH)',
        value: item.titleTh,
        required: true,
        maxLength: 200,
      },
      {
        key: `${ContentDetailTableKey.GapToImprove}-titleEn-${idx}`,
        name: 'Title (EN)',
        value: item.titleEn,
        required: true,
        maxLength: 200,
      },
      {
        key: `${ContentDetailTableKey.GapToImprove}-descTh-${idx}`,
        name: 'Description (TH)',
        value: item.descTh,
        required: true,
        maxLength: 200,
      },
      {
        key: `${ContentDetailTableKey.GapToImprove}-descEn-${idx}`,
        name: 'Description (EN)',
        value: item.descEn,
        required: true,
        maxLength: 200,
      },
    ]).flat();
    return renderTable("Before Transformation : Gap to Improve", dataSource, true, 'gapToImprove');
  }

  const StrategicObjectiveTable = () => {
    if (!contentDetail || Object.keys(contentDetail).length === 0) return null;
    const items = contentDetail[ContentDetailTableKey.StrategicObjective];
    const dataSource: DetailItem[] = items.map((item, idx) => [
      {
        key: `${ContentDetailTableKey.StrategicObjective}-header-${idx}`,
        name: (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <span>รายการที่ {idx + 1}</span>
            {isEditMode && items.length > 1 && (
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => removeListItem('strategicObjective', idx + 1)}
              >
                ลบรายการ
              </Button>
            )}
          </div>
        ),
        value: '',
      },
      {
        key: `${ContentDetailTableKey.StrategicObjective}-th-${idx}`,
        name: 'Strategic Objectives (TH)',
        value: item.objectiveTh,
        required: true,
        maxLength: 200,
      },
      {
        key: `${ContentDetailTableKey.StrategicObjective}-en-${idx}`,
        name: 'Strategic Objectives (EN)',
        value: item.objectiveEn,
        required: true,
        maxLength: 200,
      },
    ]).flat();
    return renderTable("Transformation Blueprint : Strategic Objectives", dataSource, true, 'strategicObjective');
  }

  const BusinessImpactTable = () => {
    if (!contentDetail || Object.keys(contentDetail).length === 0) return null;
    const items = contentDetail[ContentDetailTableKey.BusinessImpact];
    const dataSource: DetailItem[] = items.map((item, idx) => [
      {
        key: `${ContentDetailTableKey.BusinessImpact}-header-${idx}`,
        name: (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <span>รายการที่ {idx + 1}</span>
            {isEditMode && items.length > 1 && (
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => removeListItem('businessImpact', idx + 1)}
              >
                ลบรายการ
              </Button>
            )}
          </div>
        ),
        value: '',
      },
      {
        key: `${ContentDetailTableKey.BusinessImpact}-th-${idx}`,
        name: 'Business Impacts (TH)',
        value: item.impactTh,
        required: true,
        maxLength: 200,
      },
      {
        key: `${ContentDetailTableKey.BusinessImpact}-en-${idx}`,
        name: 'Business Impacts (EN)',
        value: item.impactEn,
        required: true,
        maxLength: 200,
      },
    ]).flat();
    return renderTable("Transformation Blueprint : Business Impacts", dataSource, true, 'businessImpact');
  }

  const EndGoalsTable = () => {
    if (!contentDetail || Object.keys(contentDetail).length === 0) return null;
    const items = contentDetail[ContentDetailTableKey.EndGoals];
    const dataSource: DetailItem[] = items.map((item, idx) => [
      {
        key: `${ContentDetailTableKey.EndGoals}-header-${idx}`,
        name: (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <span>รายการที่ {idx + 1}</span>
            {isEditMode && items.length > 1 && (
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => removeListItem('endGoals', idx + 1)}
              >
                ลบรายการ
              </Button>
            )}
          </div>
        ),
        value: '',
      },
      {
        key: `${ContentDetailTableKey.EndGoals}-th-${idx}`,
        name: 'End Goals (TH)',
        value: item.goalTh,
        required: true,
        maxLength: 200,
      },
      {
        key: `${ContentDetailTableKey.EndGoals}-en-${idx}`,
        name: 'End Goals (EN)',
        value: item.goalEn,
        required: true,
        maxLength: 200,
      },
    ]).flat();
    return renderTable("Transformation Blueprint : End Goals", dataSource, true, 'endGoals');
  }

  const PillarTable = () => {
    if (!contentDetail || Object.keys(contentDetail).length === 0) return null;
    const items = contentDetail[ContentDetailTableKey.Pillar];
    const dataSource: DetailItem[] = items.map((item, idx) => [
      {
        key: `${ContentDetailTableKey.Pillar}-header-${idx}`,
        name: (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <span>รายการที่ {idx + 1}</span>
            {isEditMode && items.length > 1 && (
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => removeListItem('pillar', idx + 1)}
              >
                ลบรายการ
              </Button>
            )}
          </div>
        ),
        value: '',
      },
      {
        key: `${ContentDetailTableKey.Pillar}-th-${idx}`,
        name: 'Key Capabilities / Pillars (TH)',
        value: item.pillarTh,
        required: true,
        maxLength: 200,
      },
      {
        key: `${ContentDetailTableKey.Pillar}-en-${idx}`,
        name: 'Key Capabilities / Pillars (EN)',
        value: item.pillarEn,
        required: true,
        maxLength: 200,
      },
    ]).flat();
    return renderTable("Transformation Blueprint : Key Capabilities / Pillars", dataSource, true, 'pillar');
  }

  // Add/remove detail step for a use case
  const addDetailStep = (useCaseIdx: number) => {
    if (!contentDetail) return;

    const updated = { ...contentDetail };
    updated.useCases = (updated.useCases || []).map((uc, idx) =>
      idx === useCaseIdx
        ? {
          ...uc,
          details: [
            ...Array.isArray(uc.details) ? uc.details : [uc.details],
            {
              currentStepTh: '',
              currentStepEn: '',
              nextMoveTh: '',
              nextMoveEn: '',
              potentialValueTh: '',
              potentialValueEn: '',
            },
          ],
        }
        : uc
    );
    updateContentDetail(updated);
  };

  const removeDetailStep = (useCaseIdx: number, detailIdx: number) => {
    if (!contentDetail) return;

    const updated = { ...contentDetail };
    updated.useCases = (updated.useCases || []).map((uc, idx) => {
      if (idx !== useCaseIdx) return uc;
      const detailsArr = Array.isArray(uc.details) ? uc.details : [uc.details];
      if (detailsArr.length <= 1) return uc;
      return {
        ...uc,
        details: detailsArr.filter((_, i) => i !== detailIdx),
      };
    });
    updateContentDetail(updated);
  };

  const UseCasesTable = () => {
    if (!contentDetail || Object.keys(contentDetail).length === 0) return null;
    const items = contentDetail[ContentDetailTableKey.UseCases];
    if (!items || items.length === 0) {
      // empty "" item to prompt user to fill in
      items.push({
        nameTh: '',
        nameEn: '',
        details: [
          {
            currentStepTh: '',
            currentStepEn: '',
            nextMoveTh: '',
            nextMoveEn: '',
            potentialValueTh: '',
            potentialValueEn: '',
          }
        ],
      });
    }
    const dataSource: DetailItem[] = items.flatMap((item, idx) => {
      const detailsArr = Array.isArray(item.details) ? item.details : [item.details];
      const detailRows = detailsArr.map((detail, dIdx) => [
        {
          key: `${ContentDetailTableKey.UseCases}-method-header-${idx}-${dIdx}`,
          name: (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <span>Use Case Details : ขั้นตอนที่ {dIdx + 1}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {isEditMode && detailsArr.length > 1 && (
                  <Button
                    type="text"
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => removeDetailStep(idx, dIdx)}
                    style={{ color: '#ff4d4f' }}
                  />
                )}
                {isEditMode && dIdx === detailsArr.length - 1 && (
                  <Button
                    type="default"
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() => addDetailStep(idx)}
                  >
                    เพิ่มขั้นตอน
                  </Button>
                )}
              </div>
            </div>
          ),
          value: '',
        },
        {
          key: `${ContentDetailTableKey.UseCases}-currentStepTh-${idx}-${dIdx}`,
          name: 'Current Step Stones (TH)',
          value: detail.currentStepTh,
          maxLength: 200,
        },
        {
          key: `${ContentDetailTableKey.UseCases}-currentStepEn-${idx}-${dIdx}`,
          name: 'Current Step Stones (EN)',
          value: detail.currentStepEn,
          maxLength: 200,
        },
        {
          key: `${ContentDetailTableKey.UseCases}-nextMoveTh-${idx}-${dIdx}`,
          name: 'Next Move (TH)',
          value: detail.nextMoveTh,
          maxLength: 200,
        },
        {
          key: `${ContentDetailTableKey.UseCases}-nextMoveEn-${idx}-${dIdx}`,
          name: 'Next Move (EN)',
          value: detail.nextMoveEn,
          maxLength: 200,
        },
        {
          key: `${ContentDetailTableKey.UseCases}-potentialValueTh-${idx}-${dIdx}`,
          name: 'Potential Value Creations (TH)',
          value: detail.potentialValueTh,
          maxLength: 200,
        },
        {
          key: `${ContentDetailTableKey.UseCases}-potentialValueEn-${idx}-${dIdx}`,
          name: 'Potential Value Creations (EN)',
          value: detail.potentialValueEn,
          maxLength: 200,
        },
      ]).flat();
      return [
        {
          key: `${ContentDetailTableKey.UseCases}-header-${idx}`,
          name: (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <span>Use Case Name (รายการที่ {idx + 1})</span>
              {isEditMode && items.length > 1 && (
                <Button
                  type="text"
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => removeListItem('useCases', idx + 1)}
                  style={{ color: '#ff4d4f' }}
                />
              )}
            </div>
          ),
          value: '',
        },
        {
          key: `${ContentDetailTableKey.UseCases}-nameTh-${idx}`,
          name: 'Use Case Name (TH)',
          value: item.nameTh,
          maxLength: 200,
        },
        {
          key: `${ContentDetailTableKey.UseCases}-nameEn-${idx}`,
          name: 'Use Case Name (EN)',
          value: item.nameEn,
          maxLength: 200,
        },
        {
          key: `${ContentDetailTableKey.UseCases}-detail-header-${idx}`,
          name: 'Use Case Details',
          value: '',
          maxLength: 200,
        },
        ...detailRows
      ];
    });
    return renderTable("Use Cases / Initiatives", dataSource, true, 'useCases');
  };

  const TechnologyTable = () => {
    if (!contentDetail || Object.keys(contentDetail).length === 0) return null;
    const tech = contentDetail[ContentDetailTableKey.Technology];
    const dataSource: DetailItem[] = [
      {
        key: `${ContentDetailTableKey.Technology}-titleTh`,
        name: 'Title (TH)',
        value: tech.titleTh,
        required: true,
        maxLength: 200,
      },
      {
        key: `${ContentDetailTableKey.Technology}-titleEn`,
        name: 'Title (EN)',
        value: tech.titleEn,
        required: true,
        maxLength: 200,
      },
    ];
    return renderTable("Technologies & Enablers", dataSource, false);
  }

  const KeyChallengeTable = () => {
    if (!contentDetail || Object.keys(contentDetail).length === 0) return null;
    const items = contentDetail[ContentDetailTableKey.KeyChallenges];
    if (!items || items.length === 0) {
      // empty "" item to prompt user to fill in
      items.push({ challengeTh: '', challengeEn: '' });
    }
    const dataSource: DetailItem[] = items.map((item, idx) => [
      {
        key: `${ContentDetailTableKey.KeyChallenges}-header-${idx}`,
        name: (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <span>รายการที่ {idx + 1}</span>
            {isEditMode && items.length > 1 && (
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => removeListItem('keyChallenges', idx + 1)}
              >
                ลบรายการ
              </Button>
            )}
          </div>
        ),
        value: '',
      },
      {
        key: `${ContentDetailTableKey.KeyChallenges}-th-${idx}`,
        name: 'Key Challenges (TH)',
        value: item.challengeTh,
        required: true,
        maxLength: 200,
      },
      {
        key: `${ContentDetailTableKey.KeyChallenges}-en-${idx}`,
        name: 'Key Challenges (EN)',
        value: item.challengeEn,
        required: true,
        maxLength: 200,
      },
    ]).flat();
    return renderTable("Technologies & Enablers : Key Challenges", dataSource, true, 'keyChallenges');
  }

  // Component to display images from asset SKs
  const TechnologyImageViewer: React.FC<{ assetSK: string }> = ({ assetSK }) => {
    const [imageUrl, setImageUrl] = useState<string>('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
      const loadImage = async () => {
        try {
          const response = await fileService.getViewUrls([assetSK]);
          if (response.urls.length > 0) {
            setImageUrl(response.urls[0].presigned_url);
          }
        } catch (error) {
          console.error('Failed to load image:', error);
        } finally {
          setLoading(false);
        }
      };

      loadImage();
    }, [assetSK]);

    if (loading) {
      return <div style={{ width: 200, height: 150, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="small" />
      </div>;
    }

    return (
      <Image 
        width={200} 
        height={150} 
        src={imageUrl || "../../src/assets/placeholder.png"} 
        alt={`Asset ${assetSK}`}
        style={{ objectFit: 'contain' }}
      />
    );
  };

  const handleImagePreview = async (file: UploadFile) => {
    if (!file.url && !file.preview) {
      if (file.response?.assetId) {
        // If it's an uploaded file with asset ID, get view URL
        try {
          const response = await fileService.getViewUrls([file.response.assetId]);
          if (response.urls.length > 0) {
            file.url = response.urls[0].presigned_url;
          }
        } catch (error) {
          console.error('Failed to get preview URL:', error);
        }
      } else if (file.originFileObj) {
        // If it's a new file being uploaded, create preview
        file.preview = await getBase64(file.originFileObj as FileType);
      }
    }

    setPreviewImage(file.url || file.preview || '');
    setPreviewImageOpen(true);
  };

  const TechnologyImage = useMemo(() => {
    if (!contentDetail || Object.keys(contentDetail).length === 0) return null;
    const imageAssetSKs = contentDetail[ContentDetailTableKey.TechnologyImage].images;
    
    if (!isEditMode) {
      return (
        <div className="table-container">
          <h3 style={{ marginBottom: 12 }}>Enabling Technology Images</h3>
          <span style={{ color: '#888', fontSize: 14 }}>มีรูปภาพทั้งหมด {imageAssetSKs.length} รูปภาพ</span>
          <div style={{ padding: 20, textAlign: 'center' }}>
            <div style={{ display: 'flex', gap: 16, justifyContent: 'flex-start' }}>
              {imageAssetSKs.map((assetSK) => (
                <TechnologyImageViewer key={assetSK} assetSK={assetSK} />
              ))}
            </div>
          </div>
        </div>
      );
    }
    else {
      return (
        <div className="table-container" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <h3 style={{ marginBottom: 12 }}>Enabling Technology Images</h3>
          <span style={{ color: '#888', fontSize: 14 }}>อัปโหลดได้สูงสุด 3 รูปภาพ (อัตราส่วน 4:3)</span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() => setCropModalVisible(true)}
              disabled={imageFileList.length >= 3}
              style={{ height: 'auto', padding: '20px', textAlign: 'center' }}
            >
              <div>
                <div style={{ fontSize: 16, marginBottom: 4 }}>เพิ่มรูปภาพ</div>
                <div style={{ fontSize: 12, color: '#666' }}>คลิกเพื่อเลือกและปรับขนาดรูปภาพ</div>
              </div>
            </Button>
            
            {imageFileList.length > 0 && (
              <Upload
                listType="picture-card"
                fileList={imageFileList}
                onPreview={handleImagePreview}
                onRemove={(file) => {
                  const newFileList = imageFileList.filter(item => item.uid !== file.uid);
                  setImageFileList(newFileList);
                  
                  // Update content detail
                  if (contentDetail) {
                    const updated = { ...contentDetail };
                    const assetSKs = newFileList
                      .filter(f => f.status === 'done' && f.uid)
                      .map(f => f.uid);
                    updated[ContentDetailTableKey.TechnologyImage].images = assetSKs;
                    updateContentDetail(updated);
                  }
                }}
                showUploadList={{
                  showPreviewIcon: true,
                  showRemoveIcon: true,
                  showDownloadIcon: false,
                }}
              />
            )}
          </div>
          
          {previewImage && (
            <Image
              wrapperStyle={{ display: 'none' }}
              preview={{
                visible: previewImageOpen,
                onVisibleChange: (visible) => setPreviewImageOpen(visible),
                afterOpenChange: (visible) => !visible && setPreviewImage(''),
              }}
              src={previewImage}
            />
          )}
        </div>
      );
    }
  }, [contentDetail, isEditMode, imageFileList, previewImage, previewImageOpen, setCropModalVisible, updateContentDetail]);

  // Handle cropped image upload
  const handleCroppedImageUpload = async (croppedFile: File) => {
    try {
      // Upload the cropped file using our file service
      const assetSKs = await fileService.uploadFiles([croppedFile]);
      
      if (assetSKs.length > 0) {
        const assetSK = assetSKs[0];
        
        // Create a new upload file entry
        const newUploadFile: UploadFile = {
          uid: assetSK, // Use asset SK as uid for tracking
          name: croppedFile.name,
          status: 'done',
          size: croppedFile.size,
          type: croppedFile.type,
          thumbUrl: URL.createObjectURL(croppedFile),
        };
        
        // Add to file list
        const newFileList = [...imageFileList, newUploadFile];
        setImageFileList(newFileList);
        
        // Update the content detail with the new asset SK
        if (contentDetail) {
          const updated = { ...contentDetail };
          updated[ContentDetailTableKey.TechnologyImage].images = [
            ...updated[ContentDetailTableKey.TechnologyImage].images,
            assetSK
          ];
          updateContentDetail(updated);
          
          // Auto-save after image upload
          const success = await saveContent();
          if (success && id && id !== "new") {
            await loadContent(id);
          }
        }
      }
    } catch (error) {
      console.error('Upload error:', error);
    }
  };

  const TimelineTable = () => {
    if (!contentDetail || Object.keys(contentDetail).length === 0) return null;
    const items = contentDetail[ContentDetailTableKey.Timeline];
    if (!items || items.length === 0) {
      // empty "" item to prompt user to fill in
      items.push({
        phaseNameTh: '',
        phaseNameEn: '',
        durationTh: '',
        durationEn: '',
        status: 'inProgress',
      });
    }
    const dataSource: DetailItem[] = items.map((item, idx) => [
      {
        key: `${ContentDetailTableKey.Timeline}-header-${idx}`,
        name: (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <span>รายการที่ {idx + 1}</span>
            {isEditMode && items.length > 1 && (
              <Button
                type="text"
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => removeListItem('timeline', idx + 1)}
              >
                ลบรายการ
              </Button>
            )}
          </div>
        ),
        value: '',
      },
      {
        key: `${ContentDetailTableKey.Timeline}-phaseNameTh-${idx}`,
        name: 'Phase Name (TH)',
        value: item.phaseNameTh,
        required: true,
        maxLength: 200,
      },
      {
        key: `${ContentDetailTableKey.Timeline}-phaseNameEn-${idx}`,
        name: 'Phase Name (EN)',
        value: item.phaseNameEn,
        required: true,
        maxLength: 200,
      },
      {
        key: `${ContentDetailTableKey.Timeline}-durationTh-${idx}`,
        name: 'Duration (TH)',
        value: item.durationTh,
        required: true,
        maxLength: 10,
      },
      {
        key: `${ContentDetailTableKey.Timeline}-durationEn-${idx}`,
        name: 'Duration (EN)',
        value: item.durationEn,
        required: true,
        maxLength: 10,
      },
      {
        key: `${ContentDetailTableKey.Timeline}-status-${idx}`,
        name: 'Status',
        value: isEditMode ? (
          <Radio.Group
            key={`timeline-status-${idx}-${item.status}`}
            value={item.status || 'inProgress'}
            onChange={async (e) => {
              const updatedDetail = { ...contentDetail };
              updatedDetail.timeline = updatedDetail.timeline.map((timelineItem, timelineIdx) =>
                timelineIdx === idx ? { ...timelineItem, status: e.target.value } : timelineItem
              );
              updateContentDetail(updatedDetail);
            }}
          >
            <Radio value="inProgress">กำลังดำเนินการ</Radio>
            <Radio value="published">เผยแพร่แล้ว</Radio>
          </Radio.Group>
        ) : (
          <div>
            {(item.status || 'inProgress') === 'inProgress' && <Checkbox checked disabled>กำลังดำเนินการ</Checkbox>}
            {(item.status || 'inProgress') === 'published' && <Checkbox checked disabled>เผยแพร่แล้ว</Checkbox>}
          </div>
        ),
        required: true,
        editable: false, // Make status field non-editable to prevent EditableCell interference
      },
    ]).flat();
    return renderTable("Timeline", dataSource, true, 'timeline');
  }

  const renderTable = (
    title: string,
    dataSource: DetailItem[],
    hasListFunction: boolean = false,
    tableType?: string
  ) => {
    const canAddMore = hasListFunction && tableType && listCounts[tableType] < 5;

    return (
      <div className="table-container">
        <div className="table-header">
          <h3>{title}</h3>
          {hasListFunction && isEditMode && (
            <Button
              type="default"
              icon={<PlusOutlined />}
              disabled={!canAddMore}
              onClick={() => tableType && addListItem(tableType)}
            >
              เพิ่มรายการ {tableType && `(${listCounts[tableType]}/5)`}
            </Button>
          )}
        </div>

        <Table<DetailItem>
          components={components}
          rowClassName={(record) => {
            if (record.key.includes('list') || record.key.includes('header')) {
              return 'header-row';
            }
            return 'editable-row';
          }}
          bordered
          dataSource={dataSource}
          columns={columns as ColumnTypes}
          pagination={false}
        />
      </div>
    );
  }

  return (
    <AdminLayout children={
      <>
        {messageContext}
        <div>
        <div className="header-container">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0, marginBottom: 16 }}>
            <h3>{id === "new" ? "แบบร่างคอนเทนต์" : metadata?.title || "Loading..."}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}>
              {id !== "new" && metadata && (
                <>
                  {metadata.updatedAt && (
                    <span style={{ color: '#888', fontSize: 14 }}>
                      อัปเดตล่าสุด {formatThaiDate(metadata.updatedAt)}
                    </span>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div>
                        {metadata.status && 
                          (isVersionView ? (
                            statusTag(metadata.status)
                          ) : (
                            statusTag(metadata.edit_status || 'draft')
                          ))}
                        {((isVersionView && metadata.status === 'published') || (!isVersionView && metadata.edit_status === 'published')) && (
                        <span style={{ color: '#888', fontSize: 14 }}>
                          เวอร์ชัน {metadata.version}
                        </span>
                        )}
                    </div>
                    {isEditMode && metadata.status === 'draft' && (
                      <span style={{ 
                        color: isAutoSaving ? '#1890ff' : '#888', 
                        fontSize: 12, 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 4 
                      }}>
                        {isAutoSaving ?? 'กำลังบันทึก...'}
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
          {(
            <div style={{ display: 'flex', gap: 12 }}>
              {!isEditMode && (
                <Button
                  type="default"
                  style={{ minWidth: 90 }}
                  onClick={handleEditClick}
                  loading={isCreatingEditableVersion}
                >
                  แก้ไข
                </Button>
              )}
              <Dropdown
                menu={{
                  items: [
                    {
                      key: 'preview-th',
                      label: 'ดูตัวอย่างเนื้อหา (TH)',
                      icon: <EyeOutlined />,
                      style: { color: "rgba(250, 140, 22, 1)" },
                      onClick: () => setDetailVisible("TH"),
                    },
                    {
                      key: 'preview-en',
                      label: 'ดูตัวอย่างเนื้อหา (EN)',
                      icon: <EyeOutlined />,
                      style: { color: "rgba(250, 140, 22, 1)" },
                      onClick: () => setDetailVisible("EN"),
                    },
                    ...(id !== "new" && canDeleteContent
                      ? [{
                        key: 'delete',
                        label: 'ลบเนื้อหา',
                        icon: <CloseOutlined />,
                        onClick: () => setDeleteModalVisible(true),
                      }]
                      : []),
                  ],
                }}
                trigger={['click']}
              >
                <Button icon={<MoreOutlined />} />
              </Dropdown>
            </div>
          )}
        </div>
        
        <div className="content-container">
          {overviewTable()}
          {problemStatementTable()}
          {painPointTable()}
          {GapToImproveTable()}
          {StrategicObjectiveTable()}
          {BusinessImpactTable()}
          {EndGoalsTable()}
          {PillarTable()}
          {UseCasesTable()}
          {TechnologyTable()}
          {KeyChallengeTable()}
          {TechnologyImage}
          {TimelineTable()}
        </div>

        {isEditMode && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 12,
            marginTop: 24,
            paddingTop: 16,
            borderTop: '1px solid #f0f0f0'
          }}>
            <Button
              size="large"
              onClick={id === "new" ? handleCancel : handleCancelEdit}
            >
              ยกเลิก
            </Button>
            <div style={{ display: 'flex', gap: 12 }}>
              {metadata?.edit_status === 'draft' && id !== "new" && (
                <Button
                  size="large"
                  onClick={handleSaveDraft}
                  loading={isAutoSaving}
                >
                  บันทึกแบบร่าง
                </Button>
              )}
              {id === "new" && (
                <Button
                  size="large"
                  onClick={() => handleSave(false)}
                  loading={isSaving}
                >
                  บันทึกแบบร่าง
                </Button>
              )}
              <Button
                type="primary"
                size="large"
                onClick={() => handleSave(true)}
                loading={isSaving}
              >
                {id === "new" ? "สร้างและเผยแพร่" : "อัปโหลด"}
              </Button>
            </div>
          </div>
        )}

        <Modal
          className="detail-modal"
          open={detailVisible !== ""}
          width="100vw"
          style={{ top: 0, maxWidth: '100%' }}
          footer={null}
          closeIcon={null}
        >
          <DetailPage data={contentDetail || undefined} languageProp={detailVisible === "" ? undefined : detailVisible} overrideId={metadata?.key} onBack={() => setDetailVisible("")} />
        </Modal>
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: 16 }}>
              <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: '20px' }} />
              <span>ยืนยันการลบรายการ</span>
            </div>
          }
          open={deleteModalVisible}
          onOk={async () => {
            if (!id || id === "new") {
              setDeleteModalVisible(false);
              return;
            }

            try {
              const success = await deleteContent(id);
              if (success) {
                setDeleteModalVisible(false);
                navigate('/admin/content-list');
              }
            } catch (error) {
              console.error('Delete error:', error);
            }
          }}
          onCancel={() => setDeleteModalVisible(false)}
          okText="ยืนยัน"
          cancelText="ยกเลิก"
          confirmLoading={isSaving}
          centered
        >
          <p>คุณต้องการลบรายการ {metadata?.key}: {metadata?.title} หรือไม่?</p>
        </Modal>
        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: 16, height: 32 }}>
              <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: '20px' }} />
              <span>ต้องการเผยแพร่คอนเทนต์นี้แทนที่เวอร์ชันปัจจุบันใช่หรือไม่?</span>
            </div>
          }
          open={publishModalVisible}
          onOk={handlePublishConfirm}
          onCancel={() => setPublishModalVisible(false)}
          okText="ยืนยัน"
          cancelText="ยกเลิก"
          confirmLoading={isSaving}
          centered
        >
          <p>คอนเทนต์เวอร์ชันนี้จะถูกแสดงบน Website ทันทีแทนเวอร์ชันปัจจุบัน</p>
        </Modal>
        
        {/* Image Crop Modal */}
        <ImageCropModal
          visible={cropModalVisible}
          onCancel={() => setCropModalVisible(false)}
          onCropComplete={handleCroppedImageUpload}
          aspectRatio={4 / 3}
        />
        </div>
      </>
    } breadcrumb={{
      items: [
        { title: "รายการคอนเทนต์" },
        { title: id === "new" ? "สร้างคอนเทนต์ใหม่" : isEditMode ? "แก้ไขคอนเทนต์" : "ดูรายการคอนเทนต์" },
        { title: id === "new" ? "คอนเทนต์ใหม่" : metadata?.title || "" }
      ],
    }} />
  );
};

export default ContentDetail;